import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import HeroSection from '../components/about/HeroSection';
import HowItWorksCard from '../components/about/HowItWorksCard';
import PromiseCard from '../components/about/PromiseCard';
import ContactForm from '../components/ContactForm';
import CopyEmailLink from '../components/CopyEmailLink';
import { getHowItWorksSteps, getPromises } from '../utils/aboutContent';

const AboutPage = () => {
  const { language, t } = useLanguage();
  const howItWorksSteps = getHowItWorksSteps(language);
  const promises = getPromises(language);

  return (
    <div className="min-h-screen bg-gray-50" data-testid="about-page">
      <SEO
        title={t('Over Ons - U&Fashion', 'About Us - U&Fashion')}
        description={t(
          'U&Fashion is een onafhankelijk fashion-platform dat Europese merken zorgvuldig cureert en presenteert met context.',
          'U&Fashion is an independent fashion platform that carefully curates and presents European brands with context.'
        )}
      />

      <div className="bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            {t(
              'Een onafhankelijk fashion-platform voor Europese merken',
              'An independent fashion platform for European brands'
            )}
          </h1>
          <p className="text-xl text-white/90">
            {t(
              'Curatie, context en doorklik. Geen ruis.',
              'Curation, context and clear paths to brands. No noise.'
            )}
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {language === 'nl' ? (
          <>
            <HeroSection
              title="Waarom U&Fashion bestaat"
              subtitle="Zorgvuldig gecureerde mode voor de vrouw van 35 tot 55. Geen tienerhypes, wel tijdloze musthaves uit de beste Europese webshops."
            />

            {/* Main Story */}
            <div className="bg-white rounded-xl shadow-md p-8 mb-8">
              <div className="space-y-6 text-gray-700 leading-relaxed">
                <p className="text-lg">
                  U&Fashion werd opgericht vanuit een duidelijke frustratie: <strong>waarom is het zo moeilijk om mode te vinden die én hip én elegant is?</strong> De grote spelers stapelen tienerhypes en fast-fashion op elkaar. De kleinere Europese merken die het écht anders doen, verdwijnen in de ruis.
                </p>

                <p className="text-lg">
                  <strong>Wij cureren dagelijks de mooiste premium fashion</strong> van de beste Europese webshops. Geen vluchtige trends, wel tijdloze musthaves — verzameld op één overzichtelijke plek voor de <strong>stijlbewuste vrouw van 35 tot 55</strong> die niet meer wil kiezen tussen &ldquo;te jong&rdquo; of &ldquo;te oud&rdquo;.
                </p>

                <div className="bg-gradient-to-r from-pink-50 to-purple-50 p-6 rounded-lg my-8">
                  <p className="text-xl font-semibold text-gray-900 text-center">
                    &ldquo;Stijl kent geen leeftijd. We verkopen zelf niets — we verbinden je met merken die we vertrouwen.&rdquo;
                  </p>
                </div>

                <p className="text-lg">
                  Wat we wel doen: <strong>doordacht selecteren</strong>, <strong>met context presenteren</strong>, en <strong>bezoekers efficiënt doorverbinden</strong> met kwaliteitsmerken. Van magazine-stijl Brand Spotlights tot zorgvuldig samengestelde categoriepagina&apos;s — alles op U&Fashion is bewust gekozen voor onze doelgroep.
                </p>
              </div>
            </div>

            {/* Transparency / Who's behind */}
            <div className="bg-white rounded-xl shadow-md p-8 mb-8" data-testid="about-transparency-nl">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Wie staat er achter U&Fashion?</h3>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p className="text-lg">
                  U&Fashion is een <strong>onafhankelijk redactioneel platform</strong>, opgericht en beheerd door <strong>Marc De Belie</strong> als particuliere affiliate. We zijn geen webshop en verkopen geen producten zelf — alle aankopen verlopen rechtstreeks bij onze partnermerken via hun officiële webshops.
                </p>
                <p className="text-base text-gray-600">
                  We werken samen met affiliate-netwerken zoals Awin, Daisycon en TradeTracker. Wanneer je via een link op deze site naar een partnermerk doorklikt en daar iets aankoopt, ontvangen wij een kleine commissie — zonder extra kosten voor jou. Deze inkomsten houden U&Fashion onafhankelijk en gratis toegankelijk.
                </p>
              </div>
            </div>

            {/* How It Works */}
            <div className="bg-white rounded-xl shadow-md p-8 mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">Hoe het werkt</h3>
              <div className="grid md:grid-cols-3 gap-8">
                {howItWorksSteps.map((step) => (
                  <HowItWorksCard key={`${step.step}-${step.title}`} {...step} />
                ))}
              </div>
            </div>

            {/* Our Promise */}
            <div className="bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 rounded-xl shadow-md p-8 mb-8 text-white">
              <h3 className="text-2xl font-bold mb-8 text-center">Onze belofte</h3>
              <div className="space-y-5">
                {promises.map((promise) => (
                  <PromiseCard key={promise.title} {...promise} />
                ))}
              </div>
            </div>

            {/* For Brands callout */}
            <div className="bg-white rounded-xl shadow-md p-8 mb-8 text-center" data-testid="about-for-brands-callout-nl">
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Voor merken</h3>
              <p className="text-lg text-gray-600 mb-6 max-w-2xl mx-auto">
                Ben je zelf een merk en geïnteresseerd in samenwerking? Bekijk onze partnership-mogelijkheden — van affiliate tot een eigen Brand Spotlight.
              </p>
              <Link
                to="/for-brands"
                className="inline-block bg-gray-900 text-white px-6 py-3 rounded-full text-sm font-medium hover:bg-gray-800 transition-colors"
              >
                Naar Voor Merken
              </Link>
            </div>

            {/* Contact */}
            <div className="bg-white rounded-xl shadow-md p-8 mb-8" data-testid="contact-section-nl">
              <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">Contact</h3>
              <p className="text-lg text-gray-600 mb-8 text-center">
                Vraag, suggestie of feedback? We lezen elk bericht persoonlijk.
              </p>
              <ContactForm />
              <p className="text-center text-sm text-gray-500 mt-6">
                Liever direct mailen?{' '}
                <CopyEmailLink
                  email="info@uandfashion.eu"
                  className="text-purple-600 underline hover:text-purple-800 cursor-pointer"
                  testId="about-contact-email-nl"
                />
              </p>
            </div>

            {/* Call to Action */}
            <div className="bg-white rounded-xl shadow-md p-8 text-center">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Klaar om te ontdekken?</h3>
              <p className="text-lg text-gray-600 mb-6">
                Begin bij de categorie die je het meest aanspreekt, of laat je leiden door onze featured Brand Spotlight.
              </p>
              <Link
                to="/"
                className="inline-block bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 text-white px-8 py-4 rounded-lg font-semibold hover:shadow-lg transform hover:scale-105 transition-all duration-300"
              >
                Start je ontdekkingsreis →
              </Link>
            </div>
          </>
        ) : (
          <>
            <HeroSection
              title="Why U&Fashion exists"
              subtitle="We select instead of stacking. We present brands with care. We don't sell anything — we connect visitors with the brands we trust."
            />

            {/* Main Story */}
            <div className="bg-white rounded-xl shadow-md p-8 mb-8">
              <div className="space-y-6 text-gray-700 leading-relaxed">
                <p className="text-lg">
                  Shopping for fashion online has become overwhelming. Endless webshops, mass products without context, and aggregators trying to show everything at once. In the middle, interesting European brands disappear — not because they aren&apos;t good, but because they aren&apos;t visible.
                </p>

                <p className="text-lg">
                  <strong>U&Fashion was founded from one observation:</strong> there is room for a platform that <strong>selects instead of stacking</strong>. A platform that consciously chooses brands, presents them with care, and lets visitors discover what fits their style in a calm environment.
                </p>

                <div className="bg-gradient-to-r from-pink-50 to-purple-50 p-6 rounded-lg my-8">
                  <p className="text-xl font-semibold text-gray-900 text-center">
                    &ldquo;We&apos;re not a webshop. We don&apos;t sell anything ourselves.&rdquo;
                  </p>
                </div>

                <p className="text-lg">
                  What we do: <strong>curate thoughtfully</strong>, <strong>present with context</strong>, and <strong>connect visitors efficiently</strong> with the brands we trust. From magazine-style Brand Spotlights to carefully composed category pages — everything on U&Fashion is intentional.
                </p>
              </div>
            </div>

            {/* Transparency / Who's behind */}
            <div className="bg-white rounded-xl shadow-md p-8 mb-8" data-testid="about-transparency-en">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Who is behind U&Fashion?</h3>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p className="text-lg">
                  U&Fashion is an <strong>independent editorial platform</strong>, founded and run by <strong>Marc De Belie</strong> as a private affiliate. We are not a webshop and don&apos;t sell any products ourselves — all purchases take place directly with our partner brands through their official online stores.
                </p>
                <p className="text-base text-gray-600">
                  We work with affiliate networks such as Awin, Daisycon and TradeTracker. When you click through to a partner brand from this site and make a purchase there, we receive a small commission — at no extra cost to you. These earnings keep U&Fashion independent and freely accessible.
                </p>
              </div>
            </div>

            {/* How It Works */}
            <div className="bg-white rounded-xl shadow-md p-8 mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">How it works</h3>
              <div className="grid md:grid-cols-3 gap-8">
                {howItWorksSteps.map((step) => (
                  <HowItWorksCard key={`${step.step}-${step.title}`} {...step} />
                ))}
              </div>
            </div>

            {/* Our Promise */}
            <div className="bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 rounded-xl shadow-md p-8 mb-8 text-white">
              <h3 className="text-2xl font-bold mb-8 text-center">Our promise</h3>
              <div className="space-y-5">
                {promises.map((promise) => (
                  <PromiseCard key={promise.title} {...promise} />
                ))}
              </div>
            </div>

            {/* For Brands callout */}
            <div className="bg-white rounded-xl shadow-md p-8 mb-8 text-center" data-testid="about-for-brands-callout-en">
              <h3 className="text-2xl font-bold text-gray-900 mb-3">For brands</h3>
              <p className="text-lg text-gray-600 mb-6 max-w-2xl mx-auto">
                Are you a brand interested in working together? Explore our partnership options — from affiliate to a dedicated Brand Spotlight.
              </p>
              <Link
                to="/for-brands"
                className="inline-block bg-gray-900 text-white px-6 py-3 rounded-full text-sm font-medium hover:bg-gray-800 transition-colors"
              >
                Go to For Brands
              </Link>
            </div>

            {/* Contact */}
            <div className="bg-white rounded-xl shadow-md p-8 mb-8" data-testid="contact-section-en">
              <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">Contact</h3>
              <p className="text-lg text-gray-600 mb-8 text-center">
                Question, suggestion or feedback? We read every message personally.
              </p>
              <ContactForm />
              <p className="text-center text-sm text-gray-500 mt-6">
                Prefer to email directly?{' '}
                <CopyEmailLink
                  email="info@uandfashion.eu"
                  className="text-purple-600 underline hover:text-purple-800 cursor-pointer"
                  testId="about-contact-email-en"
                />
              </p>
            </div>

            {/* Call to Action */}
            <div className="bg-white rounded-xl shadow-md p-8 text-center">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Ready to discover?</h3>
              <p className="text-lg text-gray-600 mb-6">
                Start with the category that interests you most, or follow our featured Brand Spotlight.
              </p>
              <Link
                to="/"
                className="inline-block bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 text-white px-8 py-4 rounded-lg font-semibold hover:shadow-lg transform hover:scale-105 transition-all duration-300"
              >
                Start your discovery →
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AboutPage;
